import { XAT_API } from '@/services/api-paths';

export type SmilieValidateMap = Record<string, { ok: boolean; url?: string }>;

/** Um par de parênteses, nome alfanumérico como no chat: (smile), (biggrin) */
const SMILIE_IN_TEXT = /\(([a-zA-Z0-9_-]+)\)/g;

const LOOKS_SMILIE_TOKEN = /\([a-zA-Z0-9_-]+\)/;

export async function fetchSmilieValidation(names: string[]): Promise<SmilieValidateMap> {
  const uniq = Array.from(new Set(names.filter(Boolean)));
  if (!uniq.length) return {};
  const res = await fetch(XAT_API.smilieValidate(uniq.join(',')), {
    cache: 'no-store',
  });
  if (!res.ok) return Object.fromEntries(uniq.map((n) => [n, { ok: false as const }]));
  const data = (await res.json()) as { results?: SmilieValidateMap };
  return data.results ?? {};
}

function collectTextNodes(root: HTMLElement): Text[] {
  const out: Text[] = [];
  const w = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
  let node: Node | null;
  while ((node = w.nextNode())) {
    if (!(node instanceof Text)) continue;
    const p = node.parentElement;
    if (p?.closest('pre, code')) continue;
    if (!LOOKS_SMILIE_TOKEN.test(node.textContent ?? '')) continue;
    out.push(node);
  }
  return out;
}

function replaceParenSmiliesInTextNode(textNode: Text, validated: SmilieValidateMap): boolean {
  const text = textNode.textContent ?? '';
  const re = new RegExp(SMILIE_IN_TEXT.source, 'g');
  const matches: { index: number; len: number; name: string }[] = [];
  let m: RegExpExecArray | null;
  while ((m = re.exec(text))) {
    matches.push({ index: m.index, len: m[0].length, name: m[1] });
  }
  if (!matches.length) return false;

  const frag = document.createDocumentFragment();
  let last = 0;
  let changed = false;

  for (const mt of matches) {
    const before = text.slice(last, mt.index);
    if (before) frag.appendChild(document.createTextNode(before));

    const vr = validated[mt.name];
    if (vr?.ok && vr.url) {
      const img = document.createElement('img');
      img.src = vr.url;
      img.alt = `(${mt.name})`;
      img.title = img.alt;
      img.width = 32;
      img.height = 32;
      img.draggable = false;
      img.loading = 'lazy';
      img.decoding = 'async';
      img.setAttribute('data-xat-smilie', mt.name);
      img.className = 'inline-block h-8 w-8 max-h-8 max-w-[32px] object-contain align-text-bottom';
      frag.appendChild(img);
      changed = true;
    } else {
      frag.appendChild(document.createTextNode(text.slice(mt.index, mt.index + mt.len)));
    }
    last = mt.index + mt.len;
  }

  const rest = text.slice(last);
  if (rest) frag.appendChild(document.createTextNode(rest));

  if (!changed) return false;
  textNode.parentNode?.replaceChild(frag, textNode);
  return true;
}

/** Converte `(codigo)` em `<img>` quando a API confirma o smilie (formato chat xat). */
export async function applyDoubleParenSmiliesToElement(root: HTMLElement): Promise<boolean> {
  const textNodes = collectTextNodes(root);
  if (!textNodes.length) return false;

  const names = new Set<string>();
  for (const tn of textNodes) {
    const t = tn.textContent ?? '';
    SMILIE_IN_TEXT.lastIndex = 0;
    let m: RegExpExecArray | null;
    while ((m = SMILIE_IN_TEXT.exec(t))) {
      names.add(m[1]);
    }
  }
  if (!names.size) return false;

  const validated = await fetchSmilieValidation(Array.from(names));
  let anyChanged = false;

  for (const tn of textNodes) {
    if (!tn.isConnected) continue;
    const ok = replaceParenSmiliesInTextNode(tn, validated);
    if (ok) anyChanged = true;
  }

  return anyChanged;
}
